import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const features = [
  {
    title: 'Traceable waste handling systems',
    description: 'Every material tracked from source to authorized end-use. Full chain of custody documentation.',
  },
  {
    title: 'Multi-material recovery expertise',
    description: 'Plastics, metals, paper, e-waste, expired stock — we handle diverse waste streams.',
  },
  {
    title: 'Regulatory compliance support',
    description: 'EPR, CPCB, and state-level compliance built into every system we design.',
  },
  {
    title: 'Transparent reporting',
    description: 'Monthly material recovery reports and operational insights for every client.',
  },
  {
    title: 'Scalable operations',
    description: 'From single-facility pilots to pan-India rollouts — our systems scale with you.',
  },
  {
    title: 'Circular economy integration',
    description: 'Operations designed to maximize material circularity and minimize landfill dependency.',
  },
];

export function WhyPunarvaa() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left — editorial */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-6">
              Why Punarvaa
            </p>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Why businesses work with Punarvaa.
            </h2>
            <p className="text-base text-gray-500 leading-relaxed mb-8">
              Waste management becomes efficient when systems are structured. With 8+ years of
              experience, Punarvaa by Patekar Recycle Solution Pvt Ltd provides end-to-end waste
              management, recycling, and resource recovery solutions for industries, retailers, and
              communities across India.
            </p>

            {/* Story behind Punarvaa */}
            <div className="bg-p-50/80 backdrop-blur-sm border border-p-100/80 shadow-sm rounded-xl p-6 mb-8">
              <p className="text-xs font-semibold tracking-widest uppercase text-p-600 mb-3">
                The Story Behind Punarvaa
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                The word <strong className="text-p-700">Punarvaa</strong> means "born again."
                Materials often lose value because they are mixed, unmanaged, or poorly tracked.
                We restore value through recovery systems.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                From factory scrap to expired inventory, our approach ensures resources remain in
                circulation, businesses stay compliant, and environmental impact is reduced.
              </p>
            </div>

            <blockquote className="bg-p-50/50 backdrop-blur-sm border-l-4 border-p-600 pl-6 py-4 rounded-r-xl">
              <p className="text-xl lg:text-2xl italic font-medium text-p-600 leading-snug">
                "Waste does not disappear. It changes form. Our role is to guide that transformation."
              </p>
            </blockquote>
          </motion.div>

          {/* Right — feature list */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <div>
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className={`pl-5 border-l-2 border-p-600 py-4 ${
                    index < features.length - 1 ? 'border-b border-b-gray-100' : ''
                  }`}
                >
                  <h3 className="text-base font-bold text-gray-900 mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
